import { inject } from 'aurelia-framework';
import { NewInstance } from 'aurelia-dependency-injection';
import {
    ValidationController,
    validateTrigger,
    ValidationRules,
    ValidateBindingBehavior,
    ValidationRenderer
} from 'aurelia-validation';

import { User } from '../models/user';
import { UserService } from '../Advertiser.SharedPortal/services/user-service';
import { Router } from 'aurelia-router';
import { StatusService } from '../Advertiser.SharedPortal/services/status-service';
import { MultiObserverService } from '../Advertiser.SharedPortal/services/multi-observer-service';
import { LoggerService } from '../Advertiser.SharedPortal/services/logger-service';
import { BrowserStorageService } from '../Advertiser.SharedPortal/services/browser-storage-service';

var config = require('../../config.js');
var safeStringify = require('fast-safe-stringify');

@inject(
    NewInstance.of(ValidationController),
    Router,
    UserService,
    StatusService,
    MultiObserverService,
    LoggerService,
    BrowserStorageService
)
export class UserConfirmation {
    constructor(
        controller,
        router,
        userService,
        statusService,
        multiObserver,
        loggerService,
        browserStorage
    ) {
        this.controller = controller;
        this.router = router;
        this.userService = userService;
        this.status = statusService;
        this.multiObserver = multiObserver;
        this.logger = loggerService;
        this.browserStorage = browserStorage;

        this.controller.validateTrigger = validateTrigger.manual;
        this.emailAddress = '';
        this.verificationCode = '';
        this.errorMessage = '';
        this.showError = false;
        this.oldPassword = '';
        this.newPassword = '';
        this.newPassword2 = '';
        this.loginUrl = '#/login';

        this.myKeypressCallback = this.keypressInput.bind(this);
    }

    activate(params, routeConfig) {
        let that = this;
        this.verificationCode = params.code;
        this.oldPassword = Buffer.from(params.password, 'base64').toString();
        this.emailAddress = Buffer.from(params.email, 'base64').toString();
        this.multiObserver.observe(this, ['newPassword', 'newPassword2'], function () {
            that.status.dirty();
        });
        window.addEventListener('keypress', this.myKeypressCallback, false);
        this.logger.debug('Logging out user (if logged in)');
        this.userService.logout(true);
        this.configStatus();
    }

    deactivate() {
        window.removeEventListener('keypress', this.myKeypressCallback);
    }

    configStatus() {
        // statuses:  dontchange, clean, dirty, working, loading, saving, success, error
        this.status.texts = [
            '',
            'Create Password',
            'Create Password',
            'Creating Password',
            'Creating Password',
            '',
            'Password Created',
            'Error'
        ];
        this.status.classes = [
            '',
            'btn-disabled',
            'btn-enabled',
            'btn-processing',
            '',
            '',
            'btn-success',
            'btn-error'
        ];
        this.status.clean();
    }

    keypressInput(e) {
        if (e.key === 'Enter') {
            this.onSubmit();
        }
    }

    confirmSignupWithPasswordChange(emailAddress, previousPassword, proposedPassword) {
        var postData = {
            action: 'ConfirmSignup',
            params: {
                AccessToken: null,
                PreviousPassword: previousPassword,
                ProposedPassword: proposedPassword,
                UserName: emailAddress
            }
        };

        return this.userService.post(config.userMgmtAPIGatewayUrl, JSON.stringify(postData));
    }

    onSubmit() {
        console.log('onSubmit()');
        let that = this;
        let errors = this.controller.validate().then(err => {
            if (err.valid) {
                this.status.working();
                this.errorMessage = '';
                this.controller.reset();
                this.logger.debug('Confirming signup with password change:', this.emailAddress);

                // Confirm signup with password change using previous password from URL
                this.confirmSignupWithPasswordChange(
                    this.emailAddress,
                    this.oldPassword,
                    this.newPassword
                )
                    .then(response => {
                        if (response && response[0] !== 'Success') {
                            that.errorMessage = response;
                            that.status.set(
                                that.status.statuses.error,
                                that.status.statuses.clean,
                                config.buttonReset
                            );
                        } else {
                            // Success - redirect to login
                            that.errorMessage = '';
                            that.status.set(
                                that.status.statuses.success,
                                that.status.statuses.clean,
                                config.buttonReset
                            );

                            setTimeout(function () {
                                that.router.navigateToRoute('login', { emailaddress: that.emailAddress });
                            }, 2000);
                        }
                    })
                    .catch(error => {
                        that.logger.error('ConfirmSignup error:', error);
                        that.errorMessage = error.message || 'An error occurred. Please try again.';
                        that.status.set(
                            that.status.statuses.error,
                            that.status.statuses.clean,
                            config.buttonReset
                        );
                    });
            } else {
                console.log('Error: ' + JSON.stringify(that.controller.errors));
            }
        });
    }

    onLogin() {
        this.router.navigateToRoute('login', { emailaddress: this.emailAddress });
    }
}

var passwordRegex =
    /(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\,><':;|_~`]).{8,}/;
var passwordFormatMessage =
    'Sorry, your {which} password is formatted incorrectly. Passwords must be at least 8 characters including one number, one upper, one lowercase letter, and a special character: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / , > < \' : ; | _ ~ `';
var passwordMatchMessage = "Sorry, your passwords don't match. Please re-enter your passwords.";

ValidationRules.ensure(p => p.oldPassword)
    .required()
    .ensure(p => p.newPassword)
    .required()
    .ensure(p => p.oldPassword)
    .matches(passwordRegex)
    .withMessage(passwordFormatMessage.replace('{which}', 'temporary'))
    .ensure(p => p.newPassword)
    .matches(passwordRegex)
    .withMessage(passwordFormatMessage.replace('{which}', 'new'))
    .ensure(p => p.newPassword2)
    .required()
    .withMessage('Re-entered Password is required')
    .ensure(p => p.newPassword2)
    .satisfies((value, obj) => value === obj.newPassword)
    .withMessage(passwordMatchMessage)
    .ensure(p => p.emailAddress)
    .required()
    .ensure(p => p.emailAddress)
    .email()
    .withMessage('Email address is improperly formated (please ensure it is all lowercase)')
    .on(UserConfirmation);
